<template>
  <div class="notes_board_container">
    <!-- Top Action Toolbar -->
    <notes-header-actions
      :active-view="activeView"
      :is-editable-view="isEditableView"
      :is-shared-active="currentSharedSection"
      :notes-board-insert="notesBoardInsert"
      :is-loading-shared="isLoadingShared"
      :is-converting-pdf="notesConvertPdfState"
      :disabled="submitted"
      @back="handleEditorBack"
      @new-note="newInsertesNotesBoardMethod"
      @toggle-shared="handleSharedToggle"
      @toggle-insert="notesBoardInsertMethod"
      @expand="enabledNotesViewPopupMethod(activeNote)"
      @export-pdf="enabledConvertedPdfPopupMethod(activeNote)"
      @delete="removedInsertedNotesDataMethod(activeNote.id)"
      @back-my-notes="backToMyNotesList"
      @done="handleEditorDone"
      @save-new="savedInsertedNotesDataMethod"
    />

    <!-- VIEW 1: Notes List (Shared or Personal Notes) -->
    <notes-list-view
      v-if="activeView === 'list'"
      :shared-notes-section="sharedNotesSection"
      :is-loading-notes="isLoadingNotes"
      :is-loading-shared="isLoadingShared"
      :visible-own-notes="visibleOwnNotes"
      :all-shared-notes-results="allSharedNotesResults"
      :note-close-state="noteCloseState"
      @select-shared-note="onSharedNoteSelect"
      @select-note="updatedNotesDetailsByIdmethod"
      @share-note="sharedNotesPopupEnabledFun"
    />

    <!-- VIEW 2: Create Mode / New Draft -->
    <notes-create-view
      v-else-if="activeView === 'create'"
      :is-loading-shared="isLoadingShared"
      :all-shared-notes-results="allSharedNotesResults"
      :notes-board-insert="notesBoardInsert"
      :title.sync="createNotesTitle"
      :content.sync="createNotesData"
      :custom-toolbar="customToolbar"
      :editor-options="editorOptions"
      @select-shared-note="onSharedNoteSelect"
    />

    <!-- VIEW 3: Edit Mode -->
    <notes-edit-view
      v-else-if="isEditableView"
      :active-note="activeNote"
      :is-active-note-locked="isActiveNoteLocked"
      :editor-key="editorKey"
      :custom-toolbar="customToolbar"
      :editor-options="editorOptions"
      @update-active-note="handleActiveNoteUpdate"
      @mark-dirty="markNoteDirty"
      @lock-requested="updatedSharedNotesNotificationMethod"
      @save-note="saveCurrentNote"
    />

    <!-- VIEW 4: Read-Only Shared Note -->
    <notes-read-only-view
      v-else-if="activeView === 'read-only'"
      :note-details="sharedNotesViewDetails"
    />

    <!-- Modals & Subcomponents -->
    <notes-delete-modal
      v-if="notesRemovedPopup"
      :loading="submitted"
      @cancel="closeRemoveItemModalFun"
      @confirm="removeModalItemConfirmFun"
    />
    <notes-pdf
      v-if="notesConvertPdfState"
      :notes-convert-pdf-d-a-t-a="notesConvertPdfData"
    />
    <notes-share
      v-if="sharedNotesPopup"
      :shared-notes-i-n-f-o="sharedNotesInfo"
    />
    <notes-view
      v-if="notesViewPopup"
      :notes-view-d-a-t-a="notesViewData"
    />
  </div>
</template>

<script>
import { Quill } from 'vue2-editor';
import { bus } from '@/main';
import NotesHeaderActions from './NotesHeaderActions.vue';
import NotesDeleteModal from './NotesDeleteModal.vue';
import NotesListView from './NotesListView.vue';
import NotesCreateView from './NotesCreateView.vue';
import NotesEditView from './NotesUpdateView.vue';
import NotesReadOnlyView from './NotesReadOnlyView.vue';
import notesConvertPdf from './notes-convert-to-pdf/index.vue';
import notesView from './notes-view-section/index.vue';
import sharedNotes from './shared-notes/index.vue';

const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'georgia', 'tahoma', 'verdana'];
Quill.register(Font, true);

const VIEW = Object.freeze({
  LIST: 'list',
  CREATE: 'create',
  MY_EDIT: 'my-edit',
  SHARED_EDIT: 'shared-edit',
  READ_ONLY: 'read-only'
});

export default {
  name: 'NotesBoard',

  components: {
    NotesHeaderActions,
    NotesDeleteModal,
    NotesListView,
    NotesCreateView,
    NotesEditView,
    NotesReadOnlyView,
    'notes-share': sharedNotes,
    'notes-pdf': notesConvertPdf,
    'notes-view': notesView
  },

  data() {
    return {
      activeView: VIEW.LIST,
      getAllNotesList: null,
      getAllNotesListResults: [],
      isLoadingNotes: false,
      notesListRequest: null,

      notesBoardInsert: false,
      createNotesData: '',
      createNotesTitle: '',

      notesRemovedPopup: false,
      notesRemovedId: null,

      notesDetailsUpdatedByIdData: {},
      sharedNotesViewDetails: {},
      detailRequestToken: 0,

      sharedNotesPopup: false,
      sharedNotesInfo: null,
      sharedNotesSection: false,
      sharedNotesList: null,
      sharedNotesListResults: [],
      sharedGroupNotesListResults: [],
      isLoadingShared: false,
      sharedNotesRequest: null,

      selfUserId: '',
      submitted: false,

      notesConvertPdfState: false,
      notesConvertPdfData: {},
      notesViewPopup: false,
      notesViewData: null,

      editorOptions: {
        placeholder: 'Description...'
      },
      customToolbar: [
        [{ font: ['arial', 'georgia', 'tahoma', 'verdana'] }],
        ['bold', 'italic', 'underline', { color: [] }, { background: [] }]
      ],

      noteDirty: false,
      saveRequest: null,
      saveQueued: false,
      queuedSaveOptions: null,
      keyUpEvent: false,
      editLockRequest: null,

      notesSearchState: '',
      noteCloseState: false,
      notesSearchResults: []
    };
  },

  computed: {
    isEditableView() {
      return [VIEW.MY_EDIT, VIEW.SHARED_EDIT].indexOf(this.activeView) !== -1;
    },

    activeNote() {
      return this.activeView === VIEW.MY_EDIT
        ? this.notesDetailsUpdatedByIdData
        : this.sharedNotesViewDetails;
    },
    
    currentSharedSection() {
      return this.activeView === VIEW.LIST
        ? this.sharedNotesSection
        : this.sharedNotesSection;
    },

    editorKey() {
      return `${this.activeView}-${this.activeNote.id || 'new'}`;
    },

    isActiveNoteLocked() {
      const editedBy = this.activeNote && this.activeNote.edited_by;
      return Boolean(
        this.activeNote &&
        this.activeNote.under_editing &&
        editedBy &&
        String(editedBy.id) !== String(this.selfUserId)
      );
    },

    allSharedNotesResults() {
      const personal = this.sharedNotesListResults.map((item) => ({ item, isGroup: false }));
      const groups = this.sharedGroupNotesListResults.map((item) => ({ item, isGroup: true }));
      return personal.concat(groups);
    },

    visibleOwnNotes() {
      return this.noteCloseState ? this.notesSearchResults : this.getAllNotesListResults;
    },

    isBodyScrollLocked() {
      return this.notesRemovedPopup || this.notesViewPopup || this.sharedNotesPopup;
    }
  },

  watch: {
    isBodyScrollLocked: {
      immediate: true,
      handler(locked) {
        this.setBodyScrollLocked(locked);
      }
    }
  },

  mounted() {
    this.selfUserId = localStorage.getItem('LoginedUserID') || '';
    this.getAllCreatedNotesListMethod();

    bus.$on('notesViewPopupBus', this.handleNotesViewPopupBus);
    bus.$on('sharedNotesBus', this.handleSharedNotesBus);
    bus.$on('notesConvertPdfStateBus', this.handleNotesConvertPdfStateBus);
  },

  beforeDestroy() {
    bus.$off('notesViewPopupBus', this.handleNotesViewPopupBus);
    bus.$off('sharedNotesBus', this.handleSharedNotesBus);
    bus.$off('notesConvertPdfStateBus', this.handleNotesConvertPdfStateBus);

    this.setBodyScrollLocked(false);

    if (this.isEditableView && this.activeNote && this.activeNote.id) {
      this.releaseEditingLock(this.activeNote.id, true);
    }
  },

  methods: {
    handleActiveNoteUpdate({ key, value }) {
      this.$set(this.activeNote, key, value);
    },

    onSharedNoteSelect({ entry, isCore }) {
      this.handleSharedNoteSelection(entry, isCore);
    },

    handleSharedToggle() {
        this.showSharedUserListMethod();
    },

    getResponseData(response, fallback) {
      if (response && response.data && response.data.data !== undefined) {
        return response.data.data;
      }
      return fallback;
    },

    toArray(value) {
      return Array.isArray(value) ? value : [];
    },

    reportError(context, error) {
      // eslint-disable-next-line no-console
      console.error('[Notes] ' + context, error);
    },

    setBodyScrollLocked(locked) {
      if (typeof document === 'undefined' || !document.body) return;
      document.body.classList.toggle('body_right_scroll_remove', Boolean(locked));
    },

    setActiveView(view) {
      this.activeView = view;
    },

    getSharedNote(item) {
      return item && item.user_notes ? item.user_notes : {};
    },

    getSharedNoteId(item) {
      return this.getSharedNote(item).id;
    },

    handleSharedNoteSelection(entry, isCoreList) {
      const item = entry.item;
      const noteId = this.getSharedNoteId(item);

      if (!noteId) return;

      if (!entry.isGroup && item.write_access === false) {
        this.writeAccessNotesInfoMethod(noteId);
        return;
      }

      if (isCoreList) {
        this.viewCoreShareNotesInfoMethod(noteId, item.is_read, item.id);
      } else {
        this.viewShareNotesInfoMethod(noteId, item.is_read, item.id);
      }
    },

    async performLoadAllNotes(resolveView) {
      this.isLoadingNotes = true;
      try {
        const response = await this.$store.dispatch('getAllNotesListFun');
        const payload = this.getResponseData(response, {});
        const results = this.toArray(payload && payload.results);

        this.getAllNotesList = payload || {};
        this.getAllNotesListResults = results;

        if (resolveView) {
          if (results.length) {
            this.setActiveView(VIEW.LIST);
          } else {
            this.notesBoardInsert = false;
            this.setActiveView(VIEW.LIST);
          }
        }
        return results;
      } catch (error) {
        this.reportError('Unable to load notes.', error);
        return [];
      } finally {
        this.isLoadingNotes = false;
        this.notesListRequest = null;
      }
    },

    loadAllNotes(options) {
      const resolveView = Boolean(options && options.resolveView);
      if (this.notesListRequest) return this.notesListRequest;
      this.notesListRequest = this.performLoadAllNotes(resolveView);
      return this.notesListRequest;
    },

    async performLoadSharedNotes() {
      this.isLoadingShared = true;
      try {
        const response = await this.$store.dispatch('getAllSharedNotesListFun');
        const payload = this.getResponseData(response, {});
        this.sharedNotesList = payload || {};
        this.sharedNotesListResults = this.toArray(payload && payload.shared_notes);
        this.sharedGroupNotesListResults = this.toArray(payload && payload.grp_shared_notes);
        return this.allSharedNotesResults;
      } catch (error) {
        this.reportError('Unable to load shared notes.', error);
        this.sharedNotesList = {};
        this.sharedNotesListResults = [];
        this.sharedGroupNotesListResults = [];
        return [];
      } finally {
        this.isLoadingShared = false;
        this.sharedNotesRequest = null;
      }
    },

    loadSharedNotes() {
      if (this.sharedNotesRequest) return this.sharedNotesRequest;
      this.sharedNotesRequest = this.performLoadSharedNotes();
      return this.sharedNotesRequest;
    },

    getAllCreatedNotesListMethod() {
      return this.loadAllNotes({ resolveView: true });
    },

    async openNoteDetails(noteId, view, readInfo) {
      if (!noteId || this.submitted) return;

      const requestToken = ++this.detailRequestToken;
      this.submitted = true;

      try {
        const response = await this.$store.dispatch('getNotesDetailsByIdFun', { id: noteId });
        if (requestToken !== this.detailRequestToken) return;

        const note = this.getResponseData(response, {});
        this.noteDirty = false;
        this.keyUpEvent = false;

        if (view === VIEW.MY_EDIT) {
          this.notesDetailsUpdatedByIdData = note || {};
        } else {
          this.sharedNotesViewDetails = note || {};
        }

        this.setActiveView(view);

        if (readInfo && readInfo.isRead === false) {
          this.sharedNotesIsReadMethod(readInfo.id);
        }
      } catch (error) {
        this.reportError('Unable to open note.', error);
      } finally {
        if (requestToken === this.detailRequestToken) {
          this.submitted = false;
        }
      }
    },

    updatedNotesDetailsByIdmethod(noteId) {
      return this.openNoteDetails(noteId, VIEW.MY_EDIT);
    },

    viewShareNotesInfoMethod(noteId, isRead, isReadId) {
      return this.openNoteDetails(noteId, VIEW.SHARED_EDIT, { isRead, id: isReadId });
    },

    viewCoreShareNotesInfoMethod(noteId, isRead, isReadId) {
      return this.openNoteDetails(noteId, { isRead, id: isReadId });
    },

    writeAccessNotesInfoMethod(noteId) {
      return this.openNoteDetails(noteId, VIEW.READ_ONLY);
    },

    async sharedNotesIsReadMethod(id) {
      if (!id) return;
      try {
        await this.$store.dispatch('isReadNotestStatusFun', {
          user: this.selfUserId,
          grp_shared_notes: id
        });
        const entry = this.allSharedNotesResults.find((item) => item.item.id === id);
        if (entry) this.$set(entry.item, 'is_read', true);
      } catch (error) {
        this.reportError('Unable to mark note read.', error);
      }
    },

    showSharedUserListMethod() {
      this.sharedNotesSection = !this.sharedNotesSection;
      this.clearNotesFilterMethod();
      if (this.sharedNotesSection) this.loadSharedNotes();
    },

    async newInsertesNotesBoardMethod() {
      const canLeave = await this.finishEditing();
      if (canLeave === false) return;
      this.notesBoardInsert = true;
      this.setActiveView(VIEW.CREATE);
    },

    notesBoardInsertMethod() {
      this.notesBoardInsert = !this.notesBoardInsert;
    },

    hasMeaningfulNoteContent(html) {
      if (!html) return false;
      const text = String(html)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
      return text.length > 0;
    },

    async savedInsertedNotesDataMethod() {
      if (this.submitted) return;

      const title = this.createNotesTitle.trim();
      const hasTitle = title.length > 0;
      const hasContent = this.hasMeaningfulNoteContent(this.createNotesData);

      if (!hasTitle && !hasContent) {
        if (this.getAllNotesListResults.length) {
          this.notesBoardInsert = false;
        } else {
          this.notesBoardInsert = false;
        }
        this.setActiveView(VIEW.LIST);
        return;
      }

      this.submitted = true;
      try {
        await this.$store.dispatch('createNewNotesDetailsFun', {
          data: this.createNotesData,
          title: title || 'Untitled note'
        });

        this.createNotesData = '';
        this.createNotesTitle = '';
        this.notesBoardInsert = false;
        await this.loadAllNotes({ resolveView: true });
      } catch (error) {
        this.reportError('Unable to create note.', error);
      } finally {
        this.submitted = false;
      }
    },

    markNoteDirty() {
      this.noteDirty = true;
    },

    syncSavedNoteIntoLists(noteId, snapshot) {
      const updateNote = (note) => {
        if (!note || String(note.id) !== String(noteId)) return;
        this.$set(note, 'title', snapshot.title);
        this.$set(note, 'data', snapshot.data);
      };

      this.getAllNotesListResults.forEach(updateNote);
      this.allSharedNotesResults.forEach((entry) => {
        updateNote(this.getSharedNote(entry.item));
      });
    },

    async performCurrentNoteSave(options) {
      const note = this.activeNote;
      if (!note || !note.id || this.isActiveNoteLocked) return;

      const force = Boolean(options && options.force);
      const releaseLock = !options || options.releaseLock !== false;

      if (!force && !this.noteDirty) {
        if (releaseLock) await this.releaseEditingLock(note.id, true);
        return;
      }

      const noteId = note.id;
      const snapshot = {
        data: note.data || '',
        title: (note.title || '').trim() || 'Untitled note'
      };

      this.noteDirty = false;

      try {
        await this.$store.dispatch('updateNewNotesDetailsFun', {
          data: { data: snapshot, id: noteId }
        });
        this.syncSavedNoteIntoLists(noteId, snapshot);
      } catch (error) {
        this.noteDirty = true;
        this.reportError('Unable to save note.', error);
        throw error;
      }

      if (releaseLock && !this.saveQueued && !this.noteDirty) {
        await this.releaseEditingLock(noteId, true);
      }
      return true;
    },

    saveCurrentNote(options) {
      if (!this.isEditableView) return Promise.resolve(true);

      if (this.saveRequest) {
        if (this.noteDirty || (options && options.force)) {
          this.saveQueued = true;
          this.queuedSaveOptions = options || this.queuedSaveOptions;
        }
        return this.saveRequest;
      }

      const currentOptions = options || {};
      this.saveQueued = false;
      this.queuedSaveOptions = null;

      this.saveRequest = this.performCurrentNoteSave(currentOptions)
        .then(() => true)
        .catch(() => false)
        .then((saved) => {
          const shouldSaveAgain = saved && this.saveQueued;
          const nextOptions = this.queuedSaveOptions || currentOptions;

          this.saveRequest = null;
          this.saveQueued = false;
          this.queuedSaveOptions = null;

          if (shouldSaveAgain) {
            return this.saveCurrentNote({
              force: true,
              releaseLock: nextOptions.releaseLock !== false
            });
          }
          return saved;
        });

      return this.saveRequest;
    },

    async updatedSharedNotesNotificationMethod(updateId) {
      if (!updateId || this.keyUpEvent || this.editLockRequest || this.isActiveNoteLocked) return;

      this.editLockRequest = this.$store.dispatch('updateNewNotesDetailsFun', {
        data: {
          data: { under_editing: true, edited_by: this.selfUserId },
          id: updateId
        }
      });

      try {
        await this.editLockRequest;
        this.keyUpEvent = true;
      } catch (error) {
        this.reportError('Lock error', error);
      } finally {
        this.editLockRequest = null;
      }
    },

    async releaseEditingLock(updateId, suppressError) {
      if (!updateId) return;

      if (this.editLockRequest) {
        try {
          await this.editLockRequest;
        } catch (_) {}
      }

      if (!this.keyUpEvent) return;

      try {
        await this.$store.dispatch('updateNewNotesDetailsFun', {
          data: {
            data: { under_editing: false, edited_by: this.selfUserId },
            id: updateId
          }
        });
      } catch (error) {
        if (!suppressError) this.reportError('Unlock error', error);
      } finally {
        this.keyUpEvent = false;
      }
    },

    async finishEditing(nextView) {
      if (this.isEditableView && this.activeNote && this.activeNote.id) {
        const saved = await this.saveCurrentNote();
        if (saved === false && this.noteDirty) return false;
        await this.releaseEditingLock(this.activeNote.id, true);
      }
      this.noteDirty = false;
      if (nextView) this.setActiveView(nextView);
      return true;
    },

    async handleEditorBack() {
      if (this.activeView === VIEW.MY_EDIT) {
        await this.newInsertesNotesBoardMethod();
      }  else {
        await this.backToSharedNotesList();
      }
    },

    async handleEditorDone() {
      await this.finishEditing(VIEW.LIST);
    },

    async backToMyNotesList() {
      const canLeave = await this.finishEditing(VIEW.LIST);
      if (canLeave === false) return;
      this.sharedNotesSection = false;
    },

    async backToSharedNotesList() {
      const canLeave = await this.finishEditing(VIEW.LIST);
      if (canLeave === false) return;
      this.sharedNotesSection = true;
      this.loadSharedNotes();
    },

    enabledNotesViewPopupMethod(data) {
      this.notesViewData = data || {};
      this.notesViewPopup = true;
    },

    enabledConvertedPdfPopupMethod(data) {
      this.notesConvertPdfData = data || {};
      this.notesConvertPdfState = true;
    },

    sharedNotesPopupEnabledFun(data) {
      this.sharedNotesInfo = data || {};
      this.sharedNotesPopup = true;
    },

    removedInsertedNotesDataMethod(removeId) {
      if (!removeId) return;
      this.notesRemovedId = removeId;
      this.notesRemovedPopup = true;
    },

    closeRemoveItemModalFun() {
      this.notesRemovedId = null;
      this.notesRemovedPopup = false;
    },

    async removeModalItemConfirmFun() {
      if (!this.notesRemovedId || this.submitted) return;
      this.submitted = true;

      try {
        await this.$store.dispatch('deleteNewNotesDetailsFun', { id: this.notesRemovedId });
        this.closeRemoveItemModalFun();
        this.notesDetailsUpdatedByIdData = {};
        this.noteDirty = false;
        this.keyUpEvent = false;
        await this.loadAllNotes({ resolveView: true });
      } catch (error) {
        this.reportError('Delete error', error);
      } finally {
        this.submitted = false;
      }
    },

    clearNotesFilterMethod() {
      this.notesSearchState = '';
      this.noteCloseState = false;
      this.notesSearchResults = [];
    },

    handleNotesViewPopupBus(value) {
      this.notesViewPopup = Boolean(value);
      if (!this.notesViewPopup) {
        this.loadAllNotes({ resolveView: false });
        if (this.sharedNotesList) this.loadSharedNotes();
      }
    },

    handleSharedNotesBus(value) {
      this.sharedNotesPopup = Boolean(value);
      if (!this.sharedNotesPopup) {
        this.loadAllNotes({ resolveView: false });
        if (this.sharedNotesList) this.loadSharedNotes();
      }
    },

    handleNotesConvertPdfStateBus(value) {
      this.notesConvertPdfState = Boolean(value);
    }
  }
};
</script>

<style lang="scss" scoped>
$bg-primary: #111111;
$text-main: #f1f3f7;

.notes_board_container {
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: $bg-primary;
  color: $text-main;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
}
</style>